/** Éditeur d’un modèle récurrent — lignes + rythme. */
import { useState } from "react";
import { Button } from "../../components/Button";
import { FxDate } from "../../components/FxDate";
import { FxSelect } from "../../components/FxSelect";
import { RichText } from "../../components/RichText";
import { useFormatters } from "../../formats/useFormatters";
import { clientSelectOption } from "../clients/format";
import type { ClientListItem } from "../clients/types";
import type { TaxRate } from "../catalog/types";
import { typedDecimal } from "../catalog/types";
import { QuoteField } from "../quotes/QuoteField";
import { QuoteLines } from "../quotes/QuoteLines";
import { QuotePdfDrawer } from "../quotes/QuotePdfDrawer";
import { printQuotePdf, quotePrintUrl } from "../quotes/quotePdf";
import { draftTotals } from "../quotes/format";
import { RecurringMenu } from "./RecurringMenu";
import { addDays } from "../quotes/types";
import { recDaysBetween, recDueDays, recFreqLabel, recStatusLabel, recStatusTone, RECURRING_FREQS, type RecurringDraft, type RecurringFreq } from "./types";
import "../quotes/quotes.css";
import "../clients/clientShow.css";

const CYCLE_PRESETS = [3, 6, 12, 24, 36];

type RecurringEditorProps = {
  companyId: number;
  draft: RecurringDraft;
  setDraft: (next: RecurringDraft) => void;
  clients: ClientListItem[];
  rates: TaxRate[];
  decimalComma: boolean;
  defaultTaxId: number | null;
  busy: boolean;
  error: string;
  onSave: () => void;
  onCancel: () => void;
  onLaunch?: () => void;
  onPause?: () => void;
  onEnd?: () => void;
  onDelete?: () => void;
  onCloneInvoice?: () => void;
  onDuplicate?: () => void;
};

export function RecurringEditor({
  companyId,
  draft,
  setDraft,
  clients,
  rates,
  decimalComma,
  defaultTaxId,
  busy,
  error,
  onSave,
  onCancel,
  onLaunch,
  onPause,
  onEnd,
  onDelete,
  onCloneInvoice,
  onDuplicate,
}: RecurringEditorProps) {
  const { money } = useFormatters();
  const q = draft.quote;
  const docId = Number(q.id) || 0;
  const [pdf, setPdf] = useState(false);
  const tot = draftTotals(q.lines, rates, decimalComma, q.discount, q.discount_kind);
  const locked = draft.status === "ended" || draft.status === "running";
  const cycleMode =
    draft.cycles_left === null
      ? "inf"
      : CYCLE_PRESETS.includes(draft.cycles_left)
        ? String(draft.cycles_left)
        : "custom";

  function setQuote<K extends keyof RecurringDraft["quote"]>(key: K, value: RecurringDraft["quote"][K]) {
    setDraft({ ...draft, quote: { ...q, [key]: value } });
  }

  return (
    <div className="fx-quote-stage">
      <form
        className="fx-quote-form fx-quote-stage__edit"
        onSubmit={(e) => {
          e.preventDefault();
          if (!locked) onSave();
        }}
      >
        <header className="fx-quote-top">
          <div>
            <button type="button" className="fx-fiche-back" onClick={onCancel}>
              ← Récurrentes
            </button>
            <div className="fx-quote-top__title">
              <h1>{draft.number || (draft.id ? "Facture récurrente" : "Nouvelle récurrence")}</h1>
              {draft.id ? <span className={`fx-status is-${recStatusTone(draft.status, draft.waiting)}`}>{recStatusLabel(draft.status, draft.waiting)}</span> : null}
            </div>
          </div>
          <div className="fx-quote-top__acts">
            <Button variant="ghost" onClick={onCancel}>
              Retour
            </Button>
            {draft.id ? (
              <RecurringMenu
                status={draft.status}
                busy={busy}
                onPdf={docId ? () => setPdf(true) : undefined}
                onCloneInvoice={onCloneInvoice}
                onDuplicate={onDuplicate}
                onLaunch={onLaunch}
                onPause={onPause}
                onEnd={onEnd}
                onDelete={onDelete}
              />
            ) : null}
            {!locked ? (
              <Button type="submit" disabled={busy}>
                Enregistrer
              </Button>
            ) : null}
          </div>
        </header>

        {draft.status === "running" && draft.waiting ? (
          <p className="fx-quote-flash is-lock" role="status">
            En attente — la première facture part le {draft.next_run_on || "—"}. Mettre en pause pour modifier.
          </p>
        ) : null}
        {draft.status === "running" && !draft.waiting ? (
          <p className="fx-quote-flash is-lock" role="status">
            Active — prochaine facture le {draft.next_run_on || "—"}. Mettre en pause pour modifier.
          </p>
        ) : null}
        {draft.status === "ended" ? (
          <p className="fx-quote-flash is-lock" role="status">
            Cette récurrence est terminée.
          </p>
        ) : null}
        {error ? <p className="fx-quote-flash is-err">{error}</p> : null}

        <section className="fx-quote-meta is-rec" aria-label="Rythme">
          <QuoteField label="Fréquence" htmlFor="rec_freq">
            <FxSelect
              id="rec_freq"
              value={draft.frequency}
              disabled={locked}
              options={RECURRING_FREQS.map((f) => ({ value: f, label: recFreqLabel(f) }))}
              onChange={(v) => setDraft({ ...draft, frequency: v as RecurringFreq })}
            />
          </QuoteField>
          <QuoteField label="Date de début" htmlFor="rec_start">
            <FxDate id="rec_start" value={draft.starts_on} disabled={locked} onChange={(v) => setDraft({ ...draft, starts_on: v })} />
          </QuoteField>
          <QuoteField label="Échéance" htmlFor="rec_due">
            <FxDate
              id="rec_due"
              value={addDays(draft.starts_on, recDueDays(draft.due_days))}
              disabled={locked}
              min={draft.starts_on}
              max={addDays(draft.starts_on, 31)}
              onChange={(v) => setDraft({ ...draft, due_days: recDueDays(recDaysBetween(draft.starts_on, v)) })}
            />
          </QuoteField>
          <QuoteField label="Cycles restants" htmlFor="rec_cycles">
            <div className="fx-rec-cycles">
              <FxSelect
                id="rec_cycles"
                value={cycleMode}
                disabled={locked}
                options={[
                  { value: "inf", label: "Sans fin" },
                  ...CYCLE_PRESETS.map((n) => ({ value: String(n), label: `${n} cycles` })),
                  { value: "custom", label: "Autre" },
                ]}
                onChange={(v) => {
                  if (v === "inf") {
                    setDraft({ ...draft, cycles_left: null });
                    return;
                  }
                  if (v === "custom") {
                    const cur = draft.cycles_left;
                    setDraft({ ...draft, cycles_left: cur && !CYCLE_PRESETS.includes(cur) ? cur : 8 });
                    return;
                  }
                  setDraft({ ...draft, cycles_left: Number(v) || 12 });
                }}
              />
              {cycleMode === "custom" ? (
                <input
                  className="fx-q-ctrl is-end"
                  inputMode="numeric"
                  disabled={locked}
                  aria-label="Nombre de cycles"
                  value={String(draft.cycles_left ?? 8)}
                  onChange={(e) => setDraft({ ...draft, cycles_left: Math.max(1, Number(e.target.value) || 1) })}
                />
              ) : null}
            </div>
          </QuoteField>
          <QuoteField label="Client" htmlFor="rec_client">
            <FxSelect
              id="rec_client"
              value={draft.client_id ? String(draft.client_id) : ""}
              disabled={locked}
              searchable
              placeholder="— Choisir —"
              options={clients.map(clientSelectOption)}
              onChange={(v) => setDraft({ ...draft, client_id: Number(v) || 0, quote: { ...q, client_id: Number(v) || 0 } })}
            />
          </QuoteField>
          <QuoteField label="Remise" htmlFor="rec_off">
            <div className="fx-q-unit">
              <input
                id="rec_off"
                className="fx-q-ctrl is-end"
                value={q.discount}
                disabled={locked}
                inputMode="decimal"
                placeholder="0"
                onChange={(e) => setQuote("discount", typedDecimal(e.target.value, decimalComma))}
              />
              <FxSelect
                plain
                value={q.discount_kind}
                disabled={locked}
                options={[
                  { value: "pct", label: "%" },
                  { value: "eur", label: "€" },
                ]}
                onChange={(v) => setQuote("discount_kind", v === "eur" ? "eur" : "pct")}
              />
            </div>
          </QuoteField>
        </section>

        <QuoteLines
          companyId={companyId}
          lines={q.lines}
          rates={rates}
          decimalComma={decimalComma}
          locked={locked}
          defaultTaxId={defaultTaxId}
          onChange={(lines) => setQuote("lines", lines)}
        />

        <div className="fx-quote-recap">
          <div className="fx-quote-legal">
            <RichText
              label="Conditions"
              compact
              disabled={locked}
              value={q.terms_html}
              resetKey={`${draft.id ?? "new"}-terms`}
              onChange={(html) => setQuote("terms_html", html)}
            />
            <RichText
              label="Pied de page"
              compact
              disabled={locked}
              value={q.footer_html}
              resetKey={`${draft.id ?? "new"}-footer`}
              onChange={(html) => setQuote("footer_html", html)}
            />
          </div>
          <table className="fx-quote-tot">
            <tbody>
              <tr>
                <th>Sous-total HT</th>
                <td>{money(tot.sub)}</td>
              </tr>
              {tot.off > 0 ? (
                <tr>
                  <th>Remise</th>
                  <td>− {money(tot.off)}</td>
                </tr>
              ) : null}
              <tr>
                <th>Total HT</th>
                <td>{money(tot.ht)}</td>
              </tr>
              <tr>
                <th>TVA</th>
                <td>{money(tot.tax)}</td>
              </tr>
              <tr className="is-ttc">
                <th>Total TTC</th>
                <td>{money(tot.ttc)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </form>
      {pdf && docId ? (
        <QuotePdfDrawer
          src={quotePrintUrl(docId)}
          onPrint={() => printQuotePdf(docId, undefined, draft.number || "Récurrence")}
          onClose={() => setPdf(false)}
        />
      ) : null}
    </div>
  );
}
